import { Suspense, lazy, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useConfirm } from '@/shared/hooks';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useGanttModel } from '../../hooks/useGanttModel';
import { useGanttScheduleGate } from '../../hooks/useGanttScheduleGate';
import {
  useAddTaskDependency,
  useRemoveTaskDependency,
} from '../../hooks/useGanttDependencyMutations';
import { useOpenTask } from '../../hooks/useOpenTask';
import { GanttToolbar } from './GanttToolbar';
import { GanttDragFeedback } from './GanttDragFeedback';
import type { GanttGroupBy, GanttZoom, TaskFilters } from '../../types';

const GanttChart = lazy(() => import('./GanttChart').then((m) => ({ default: m.GanttChart })));

interface LinkEndpoints {
  predecessorTaskId: string;
  successorTaskId: string;
}

interface TaskGanttViewProps {
  projectId: string;
  boardId: string;
  filters: TaskFilters;
  showCompleted: boolean;
  onShowCompletedChange: (value: boolean) => void;
}

function GanttLoading() {
  return (
    <div data-testid="tasks-gantt-loading" className="flex h-full flex-col gap-2 p-4">
      {Array.from({ length: 8 }).map((_, index) => (
        <Skeleton key={index} className="h-9 w-full" />
      ))}
    </div>
  );
}

export function TaskGanttView({
  projectId,
  boardId,
  filters,
  showCompleted,
  onShowCompletedChange,
}: TaskGanttViewProps) {
  const { t } = useTranslation('tasks');

  const [zoom, setZoom] = useState<GanttZoom>('week');
  const [groupBy, setGroupBy] = useState<GanttGroupBy>('none');
  const [scrollToTodayNonce, setScrollToTodayNonce] = useState(0);
  const openTask = useOpenTask();

  const { model, isLoading, isError, isEmpty } = useGanttModel({
    projectId,
    boardId,
    filters,
    groupBy,
    showCompleted,
  });

  const gate = useGanttScheduleGate(projectId, model);
  const addDependency = useAddTaskDependency(projectId);
  const removeDependency = useRemoveTaskDependency(projectId);

  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  function handleLinkCreate({ predecessorTaskId, successorTaskId }: LinkEndpoints) {
    addDependency.mutate({ successorTaskId, predecessorTaskId });
  }

  async function handleLinkDelete({ predecessorTaskId, successorTaskId }: LinkEndpoints) {
    const link = model.links.find(
      (candidate) => candidate.from === predecessorTaskId && candidate.to === successorTaskId,
    );
    if (!link) return;
    const { confirmed } = await confirm({
      title: t('gantt.dependency.confirmDelete.title'),
      description: t('gantt.dependency.confirmDelete.description'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    removeDependency.mutate({ successorTaskId, dependencyId: link.id });
  }

  return (
    <div data-testid="tasks-gantt-view" className="flex h-full flex-col">
      {!isError && (
        <GanttToolbar
          zoom={zoom}
          onZoomChange={setZoom}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          showCompleted={showCompleted}
          onShowCompletedChange={onShowCompletedChange}
          onTodayClick={() => setScrollToTodayNonce((value) => value + 1)}
        />
      )}

      <GanttDragFeedback
        pendingChange={gate.pendingChange}
        conflicts={gate.conflicts}
        onAccept={gate.accept}
        onReject={gate.reject}
      />

      <div className="relative min-h-0 flex-1">
        {isError ? (
          <p
            data-testid="tasks-gantt-error-message"
            className="flex h-full items-center justify-center p-8 text-sm text-muted-foreground"
          >
            {t('gantt.error')}
          </p>
        ) : isLoading ? (
          <GanttLoading />
        ) : isEmpty ? (
          <div
            data-testid="tasks-gantt-empty-state"
            className="flex h-full flex-col items-center justify-center gap-1 p-8 text-center"
          >
            <p className="text-sm font-medium text-foreground">{t('gantt.empty.title')}</p>
            <p className="text-sm text-muted-foreground">{t('gantt.empty.description')}</p>
          </div>
        ) : (
          <Suspense fallback={<GanttLoading />}>
            <GanttChart
              model={model}
              zoom={zoom}
              labels={{
                name: t('gantt.columns.name'),
                assignee: t('gantt.columns.assignee'),
                today: t('gantt.today'),
              }}
              scrollToTodayNonce={scrollToTodayNonce}
              revertNonce={gate.revertNonce}
              onScheduleEditing={gate.onScheduleEditing}
              onScheduleChange={gate.onScheduleChange}
              onTaskClick={openTask}
              onLinkCreate={handleLinkCreate}
              onLinkDelete={handleLinkDelete}
            />
          </Suspense>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        title={confirmOptions?.title ?? ''}
        description={confirmOptions?.description}
        confirmLabel={confirmOptions?.confirmLabel}
        cancelLabel={confirmOptions?.cancelLabel}
        variant={confirmOptions?.variant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}
